import React, { Fragment, useRef, useEffect, useCallback } from "react";
import { toJS } from "mobx";
import { observer } from "mobx-react-lite";
import classNames from "classnames";

import { ExpandableKeys } from "../../store/core";
import { MenuData, MenuItem } from "../../api/menu";
import menuStore from "../../store/menu";

import styles from "./Menu.module.scss";
import MenuItemComponent from "./MenuItem";

interface IMenuListProps {
  className?: string;
  style?: React.CSSProperties;
  menu: MenuData;
  expandableKeys?: ExpandableKeys;
  level: number;
}

let enterMouseTimer = null;

export const MenuList = observer<IMenuListProps, HTMLUListElement>(
  React.forwardRef(({ className, style, menu, expandableKeys, level }, ref) => {
    const liRefs = useRef([]);
    const menusRef = useRef([]);
    const leavingTimer = useRef(null);

    const hideMenus = () => {
      menusRef.current?.forEach((el, i) => {
        el.style.position = "static";
        el.style.opacity = "0";
        el.style.display = "none";
        el.style.left = "";
        el.style.top = "";
        el.parentElement.style.overflowY = "auto";
        el.dataset.level = "";
      });
    };

    const resetMenus = () => {
      expandableKeys.setKeys([]);
      hideMenus();
    };

    useEffect(() => {
      if (expandableKeys.keys.length === 0 && menuStore.menuIsCollapsed.value) {
        hideMenus();
      }
    }, [expandableKeys.keys]);

    useEffect(() => {
      const onMouseout = (e) => {
        e = e ? e : window.event;
        var from = e.relatedTarget || e.toElement;
        if (!from || from.nodeName == "HTML") {
          menuStore.setElementMouseOn(null);
          resetMenus();
        }
      };

      if (level === 0) {
        document.addEventListener("mouseout", onMouseout);
      }
      return () => {
        document.removeEventListener("mouseout", onMouseout);
      };
    }, []);

    const insideLevel = level + 1;

    const toggleExpand = (e, item, index) => {
      if (item.items.length > 0 && !menuStore.menuIsCollapsed.value) {
        const ul = menusRef.current[index];
        ul.style.opacity = "1";
        let keys = toJS(expandableKeys.keys);
        if (keys.includes(item.id)) {
          keys = keys.filter((key) => key !== item.id);
          ul.style.display = "none";
        } else {
          keys.push(item.id);
          ul.style.display = "block";
        }
        expandableKeys.setKeys(keys);
      }
    };

    useEffect(() => {
      resetMenus();
      menuStore.setElementMouseOn(null);
    }, [menuStore.menuIsCollapsed.value]);

    useEffect(() => {
      if (menuStore.isResetMenu.value && menuStore.menuIsCollapsed.value) {
        leavingTimer.current = setTimeout(() => {
          if (!menuStore.elementMouseOn) {
            resetMenus();
            menuStore.isResetMenu.setFalse();
          }
        }, 700);
      }
    }, [menuStore.isResetMenu.value, menuStore.menuIsCollapsed.value]);

    const onHandleMouseEnter = (e, item: MenuItem, index: number) => {
      clearTimeout(leavingTimer.current);
      clearTimeout(enterMouseTimer);
      leavingTimer.current = null;
      enterMouseTimer = null;
      menuStore.setElementMouseOn(e.currentTarget);
      menuStore.isResetMenu.setFalse();
      item.keys.setKeys([]);
      enterMouseTimer = setTimeout(() => {
        if (
          menuStore.menuIsCollapsed.value &&
          !expandableKeys.keys.includes(item.id) &&
          menuStore.elementMouseOn
        ) {
          resetMenus();
          const rect = menuStore.elementMouseOn.getBoundingClientRect();
          const ul = menusRef.current[index];
          expandableKeys.setKeys([item.id]);

          if (ul) {
            ul.style.display = "block";
            ul.style.position = "fixed";
            ul.style.opacity = "1";
            ul.style.zIndex = "1";

            const ulRect = ul.getBoundingClientRect();
            const liRect = liRefs.current[index].getBoundingClientRect();
            ul.style.left = rect.left + rect.width + "px";
            ul.style.top =
              (ulRect.height + e.clientY >= window.innerHeight
                ? Math.max(0, window.innerHeight - ulRect.height)
                : liRect.top) + "px";
          }
        }
      }, 200);
    };
    const onHandleMouseLeave = () => { 
      clearTimeout(enterMouseTimer);
      enterMouseTimer = null;
    }

    const onClickResetMenu = () => {
      if (menuStore.menuIsCollapsed.value) {
        resetMenus();
        menuStore.setElementMouseOn(null);
      }
    };
    const onCheckReset = () => {
      if (menuStore.menuIsCollapsed.value) {
        if (menuStore.isResetMenu.value) {
          clearTimeout(leavingTimer.current);
          menuStore.isResetMenu.setFalse();
        }
      }
    };

    return (
      <ul
        ref={ref}
        className={classNames(styles["menu__list"], {
          className: className,
          [styles["menu__list--root"]]: insideLevel === 1,
          [styles["menu__list--child"]]: insideLevel > 1,
        })}
        style={style}
        onClick={onClickResetMenu}
      >
        {menu.map((item, index) => {
          return (
            <Fragment key={item?.id}>
              <MenuItemComponent
                ref={(ref) => (liRefs.current[index] = ref)}
                item={item}
                expanded={expandableKeys.keys.includes(item.id)}
                menuIsCollapsed={menuStore.menuIsCollapsed.value}
                level={insideLevel}
                idx={index}
                onClick={(e) => toggleExpand(e, item, index)}
                onMouseEnter={(e) => onHandleMouseEnter(e, item, index)}
                onMouseLeave={onHandleMouseLeave}
                onCheckReset={onCheckReset}
              />
              {item.items.length ? (
                <MenuList
                  ref={(ref) => (menusRef.current[index] = ref)}
                  menu={item.items}
                  expandableKeys={item?.keys}
                  level={insideLevel}
                />
              ) : null}
            </Fragment>
          );
        })}
      </ul>
    );
  }),
);

export default MenuList;
